import ApplicationController from "../../application_component_controller";
import URL from "hb/lib/url";
import { fetchTurbo } from "hb/lib/fetch";
import { useListen } from "hb/insights/use_listen";

export default class extends ApplicationController {
  static targets = [
    "description",
    "currentQuery",
    "currentTs",
    "streamIds",
    "submitBtn",
    "result",
    "refreshIcon",
    "pendingTemplate",
  ];

  static values = {
    suggestionsUrl: String,
  };

  connect() {
    useListen(this);
    const form = this.element.querySelector("form");
    if (form) {
      this.listen("submit", this.syncContext, form);
      this.listen("turbo:submit-start", this.onSubmitStart, form);
      this.listen("turbo:submit-end", this.onSubmitEnd, form);
    }
    this.listenWindow("queryStreamChanged", this.onStreamChange);

    // Always fetch — the skeleton placeholders in the server render need to
    // be swapped for either trait-aware suggestions (when streams have
    // traits) or the defaults (when they don't, or when no streams selected).
    this.onStreamChange({ streamIds: this.initialStreamIds() });
  }

  initialStreamIds() {
    const raw =
      URL.params().get("stream_ids") ||
      (this.hasStreamIdsTarget && this.streamIdsTarget.value) ||
      "";
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  disconnect() {
    this.suggestionsAbort?.abort();
  }

  onStreamChange(detail) {
    this.streamIds = detail?.streamIds || [];
    this.fetchSuggestions();
  }

  refreshSuggestions(event) {
    event.preventDefault();
    if (this.hasRefreshIconTarget) {
      this.refreshIconTarget.classList.remove("rotating");
      void this.refreshIconTarget.offsetWidth;
      this.refreshIconTarget.classList.add("rotating");
    }
    this.fetchSuggestions({ listOnly: true });
  }

  fetchSuggestions({ listOnly = false } = {}) {
    if (!this.hasSuggestionsUrlValue) return;

    this.suggestionsAbort?.abort();
    this.suggestionsAbort = new AbortController();

    const params = { streamIds: JSON.stringify(this.streamIds || []) };
    if (listOnly) params.listOnly = 1;

    fetchTurbo({
      path: this.suggestionsUrlValue,
      method: "GET",
      params,
      signal: this.suggestionsAbort.signal,
    });
  }

  onSubmitStart() {
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    if (this.hasDescriptionTarget) this.descriptionTarget.disabled = true;
    if (this.hasResultTarget && this.hasPendingTemplateTarget) {
      this.resultTarget.replaceChildren(
        this.pendingTemplateTarget.content.cloneNode(true),
      );
    }
  }

  onSubmitEnd() {
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.disabled = false;
      this.descriptionTarget.focus();
    }
  }

  syncContext() {
    if (this.hasCurrentQueryTarget) {
      const editor = document.querySelector(
        ".insights--query-component textarea#query",
      );
      if (editor) this.currentQueryTarget.value = editor.value;
    }
    if (this.hasCurrentTsTarget) {
      this.currentTsTarget.value = URL.params().get("ts") || "";
    }
    if (this.hasStreamIdsTarget) {
      const fromUrl = URL.params().get("stream_ids");
      if (fromUrl !== null) this.streamIdsTarget.value = fromUrl;
    }
  }

  useExample(event) {
    const text = event.currentTarget.dataset.text;
    if (!text) return;
    this.descriptionTarget.value = text;
    this.descriptionTarget.focus();
  }

  submitRefineClick(event) {
    if (!this.descriptionTarget.value.trim()) {
      event.preventDefault();
    }
  }

  submitOnCmdEnter(event) {
    if (event.key !== "Enter") return;
    if (!(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    if (!this.descriptionTarget.value.trim()) return;
    this.element.querySelector("form")?.requestSubmit();
  }
}
